import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Search, MessageSquare, MapPin, Languages, Building2 } from 'lucide-react';
import { getOrCreateChatRoom } from '../../utils/chatUtils';

// 🌏 해외 계정용 기공소찾기 (영문) — globalOptIn+isActive 기공소만 노출 (글로벌 기획서 §2)
function GlobalFindLabs({ user }) {
    const navigate = useNavigate();
    const [labs, setLabs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [keyword, setKeyword] = useState('');
    const [chatBusy, setChatBusy] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const snap = await getDocs(query(
                    collection(db, 'labAdvertisements'),
                    where('globalOptIn', '==', true),
                    where('isActive', '==', true),
                ));
                setLabs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (e) {
                console.error('글로벌 기공소 목록 로드 실패:', e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const startChat = async (lab) => {
        const myUid = user?.companyId || user?.uid;
        if (!myUid) return;
        setChatBusy(lab.id);
        try {
            const { roomId } = await getOrCreateChatRoom(myUid, lab.labId || lab.id, 'global_find');
            navigate(`/chat?room=${roomId}`);
        } catch (e) {
            console.error('채팅 시작 실패:', e);
            alert('Could not start chat. Please try again.');
        } finally {
            setChatBusy(null);
        }
    };

    const visible = labs.filter(l => {
        if (!keyword.trim()) return true;
        const hay = `${l.businessName} ${(l.specialties || []).join(' ')} ${l.introductionEn || ''} ${l.introduction || ''}`.toLowerCase();
        return hay.includes(keyword.trim().toLowerCase());
    });

    return (
        <div style={{ maxWidth: 860, margin: '0 auto', padding: 20 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1E2230', margin: '10px 0 4px' }}>Find a Korean Dental Lab</h1>
            <p style={{ fontSize: 14, color: '#5A6072', margin: '0 0 18px' }}>
                Verified labs open to international cases. Discuss your cases in chat — tap Translate to read any message in your language.
            </p>

            <div style={{ position: 'relative', marginBottom: 18 }}>
                <Search size={16} color="#94A3B8" style={{ position: 'absolute', left: 12, top: 12 }} />
                <input
                    value={keyword} onChange={e => setKeyword(e.target.value)}
                    placeholder="Search by specialty (zirconia, implant, veneer…)"
                    style={{ width: '100%', padding: '10px 12px 10px 36px', border: '1px solid #E4E6EE', borderRadius: 10, fontSize: 14, boxSizing: 'border-box' }}
                />
            </div>

            {loading && <div style={{ color: '#5A6072', padding: 30 }}>Loading labs…</div>}
            {!loading && visible.length === 0 && (
                <div style={{ textAlign: 'center', color: '#94A3B8', padding: '50px 0' }}>
                    <Building2 size={36} />
                    <div style={{ marginTop: 10, fontSize: 14 }}>No labs found yet. Please check back soon.</div>
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                {visible.map(lab => (
                    <div key={lab.id} style={{ background: '#fff', border: '1px solid #E4E6EE', borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {lab.profileImageUrl
                                ? <img src={lab.profileImageUrl} alt="" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover' }} />
                                : <div style={{ width: 44, height: 44, borderRadius: 10, background: '#EEF0FA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Building2 size={20} color="#4F52C7" /></div>}
                            <div>
                                <div style={{ fontSize: 15.5, fontWeight: 800, color: '#1E2230' }}>{lab.businessName || 'Dental Lab'}</div>
                                <div style={{ fontSize: 12, color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <MapPin size={11} />{lab.sido || 'Korea'}
                                    <Languages size={11} style={{ marginLeft: 6 }} />{lab.globalLanguages || 'English'}
                                </div>
                            </div>
                        </div>
                        {(lab.specialties || []).length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                {lab.specialties.slice(0, 4).map(s => (
                                    <span key={s} style={{ fontSize: 11, fontWeight: 700, background: '#EEF0FA', color: '#4F52C7', padding: '2px 8px', borderRadius: 99 }}>{s}</span>
                                ))}
                            </div>
                        )}
                        <p style={{ fontSize: 13, color: '#5A6072', lineHeight: 1.6, margin: 0, flex: 1 }}>
                            {lab.introductionEn || lab.introduction || ''}
                        </p>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => navigate(`/lab/${lab.labId || lab.id}`)}
                                style={{ flex: 1, padding: '9px 0', border: '1px solid #E4E6EE', background: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#5A6072', cursor: 'pointer' }}>
                                View profile
                            </button>
                            <button onClick={() => startChat(lab)} disabled={chatBusy === lab.id}
                                style={{ flex: 1, padding: '9px 0', border: 'none', background: '#4F52C7', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                                <MessageSquare size={14} />{chatBusy === lab.id ? 'Opening…' : 'Chat'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default GlobalFindLabs;
